import React, { useState, useEffect } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { Box, Drawer, List, ListItem, ListItemButton, ListItemText, ListItemIcon, Typography, Divider, Avatar, Chip } from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import SearchIcon from "@mui/icons-material/Search";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import AssignmentIcon from "@mui/icons-material/Assignment";
import BarChartIcon from "@mui/icons-material/BarChart";
import LogoutIcon from "@mui/icons-material/Logout";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Suggestions from "./pages/Suggestions";
import Tasks from "./pages/Tasks";
import Report from "./pages/Report";
import SEO from "./pages/SEO";

const DRAWER_WIDTH = 250;

const theme = createTheme({
  palette: {
    mode: "dark",
    primary: { main: "#6366f1" },
    background: { default: "#080c14", paper: "#0d1424" },
  },
  typography: { fontFamily: "'DM Sans', sans-serif", allVariants: { color: "#ffffff" } },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 8, textTransform: "none", fontWeight: 600 },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: { borderRadius: 12 },
      },
    },
  },
});

const ownerNav = [
  { label: "Dashboard", icon: <DashboardIcon fontSize="small" />, key: "dashboard" },
  { label: "Suggestions", icon: <LightbulbIcon fontSize="small" />, key: "suggestions" },
  { label: "Tasks", icon: <AssignmentIcon fontSize="small" />, key: "tasks" },
  { label: "SEO", icon: <SearchIcon fontSize="small" />, key: "seo" },
];

const employeeNav = [
  { label: "Dashboard", icon: <DashboardIcon fontSize="small" />, key: "dashboard" },
  { label: "My Tasks", icon: <AssignmentIcon fontSize="small" />, key: "tasks" },
  { label: "Submit Report", icon: <BarChartIcon fontSize="small" />, key: "report" },
];

export default function App() {
  const [authed, setAuthed] = useState(false);
  const [role, setRole] = useState("");
  const [page, setPage] = useState("dashboard");
  const [userName, setUserName] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    const savedRole = localStorage.getItem("role");
    const savedName = localStorage.getItem("user_name");
    if (token && savedRole) {
      setAuthed(true);
      setRole(savedRole);
      setUserName(savedName || "");
    }
  }, []);

  const handleLogin = (userRole, name) => {
    setAuthed(true);
    setRole(userRole);
    setUserName(name || "");
    setPage("dashboard");
  };

  const handleLogout = () => {
    localStorage.clear();
    setAuthed(false);
    setRole("");
    setPage("dashboard");
  };

  if (!authed) return (
    <ThemeProvider theme={theme}>
      <Login onLogin={handleLogin} />
    </ThemeProvider>
  );

  const nav = role === "owner" ? ownerNav : employeeNav;

  const renderPage = () => {
    switch (page) {
      case "dashboard": return <Dashboard />;
      case "suggestions": return <Suggestions />;
      case "tasks": return <Tasks />;
      case "report": return <Report />;
      case "seo": return <SEO />;
      default: return <Dashboard />;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap" rel="stylesheet" />
      <Box sx={{ display: "flex", minHeight: "100vh", background: "#080c14" }}>
        <Drawer variant="permanent" sx={{
          width: DRAWER_WIDTH,
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            background: "#0d1424",
            border: "none",
            borderRight: "1px solid rgba(99,102,241,0.15)",
            display: "flex",
            flexDirection: "column",
          }
        }}>
          <Box sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" gap={1.5} mb={0.5}>
              <Box sx={{
                width: 32, height: 32, borderRadius: "8px",
                background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                display: "flex", alignItems: "center", justifyContent: "center"
              }}>
                <AutoAwesomeIcon sx={{ fontSize: 16, color: "#fff" }} />
              </Box>
              <Typography variant="h6" fontWeight={700} color="#fff" letterSpacing="-0.3px">
                TechtideLLC
              </Typography>
            </Box>
            <Typography variant="caption" color="rgba(255,255,255,0.3)" ml={6}>
              Ops Manager
            </Typography>
          </Box>

          <Box sx={{ mx: 2, mb: 2 }}>
            <Box sx={{
              background: "rgba(99,102,241,0.08)",
              border: "1px solid rgba(99,102,241,0.2)",
              borderRadius: "10px", p: 1.5, pb: 1,
              display: "flex", alignItems: "center", gap: 1.5
            }}>
              <Avatar sx={{
                width: 32, height: 32, fontSize: 13, fontWeight: 700,
                background: "linear-gradient(135deg, #6366f1, #8b5cf6)"
              }}>
                {userName.charAt(0).toUpperCase() || "O"}
              </Avatar>
              <Box>

                <Chip label={role.toUpperCase()} size="small" sx={{
                  height: 16, fontSize: 12, fontWeight: 700, letterSpacing: "0.5px",
                  background: "rgba(99,102,241,0.3)", color: "#a5b4fc",
                  "& .MuiChip-label": { px: 1, pb: 0 }
                }} />
              </Box>
            </Box>
          </Box>

          <Divider sx={{ borderColor: "rgba(99,102,241,0.1)", mx: 2 }} />

          <List sx={{ flex: 1, pt: 2, px: 1 }}>
            <Typography variant="caption" color="rgba(255,255,255,0.2)" fontWeight={600}
              letterSpacing="1px" sx={{ px: 1.5, mb: 1, display: "block", fontSize: 10 }}>
              NAVIGATION
            </Typography>
            {nav.map((item) => (
              <ListItem key={item.key} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  selected={page === item.key}
                  onClick={() => setPage(item.key)}
                  sx={{
                    borderRadius: "8px", py: 1, px: 1.5,
                    "&.Mui-selected": {
                      background: "linear-gradient(135deg, rgba(99,102,241,0.2), rgba(139,92,246,0.1))",
                      border: "1px solid rgba(99,102,241,0.3)",
                      "& .MuiListItemIcon-root": { color: "#6366f1" },
                      "& .MuiListItemText-primary": { color: "#fff", fontWeight: 600 },
                    },
                    "&:hover": { background: "rgba(255,255,255,0.04)" },
                    "&:not(.Mui-selected)": { border: "1px solid transparent" },
                  }}
                >
                  <ListItemIcon sx={{ color: "rgba(255,255,255,0.35)", minWidth: 32 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.label}
                    primaryTypographyProps={{ fontSize: 14, color: "rgba(255,255,255,0.5)" }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          <Divider sx={{ borderColor: "rgba(99,102,241,0.1)", mx: 2 }} />
          <List sx={{ px: 1, py: 1.5 }}>
            <ListItem disablePadding>
              <ListItemButton onClick={handleLogout} sx={{
                borderRadius: "8px", py: 1, px: 1.5,
                "&:hover": { background: "rgba(239,68,68,0.08)" }
              }}>
                <ListItemIcon sx={{ color: "rgba(239,68,68,0.6)", minWidth: 32 }}>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Logout"
                  primaryTypographyProps={{ fontSize: 14, color: "rgba(239,68,68,0.6)" }}
                />
              </ListItemButton>
            </ListItem>
          </List>
        </Drawer>

        <Box sx={{ flex: 1, overflow: "auto",
          background: "radial-gradient(ellipse at 20% 0%, rgba(99,102,241,0.06) 0%, transparent 60%), #080c14"
        }}>
          <Box sx={{ p: 3, pt: 2, maxWidth: "100%" }}>
            {renderPage()}
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
